import { useEffect } from "react";
import { Navigate } from "react-router-dom";
import useAuth from "hooks/useAuth";
import { AuthModal } from "components/AuthModal"; // Modal component for authentication
import { RequireAuthProps } from "models/requireAuth"; // Type definition for the props

/**
 * RequireAuth component ensures that the user is authenticated before accessing the children components.
 * It also handles displaying an authentication modal and locking/unlocking scroll when necessary.
 */
export const RequireAuth: React.FC<RequireAuthProps> = (props) => {
  // Destructuring props to access modal-related settings and children
  const { isModal, children, isFullPage, isScrollAvailable } = props;

  // Accessing authentication context to get the current authentication status
  const auth: any = useAuth();

  // Checking if the user is authenticated
  const isLoggedIn: boolean = auth && auth.isAuthenticated;

  // useEffect hook to disable scrolling when the authentication modal is displayed
  useEffect(() => {
    // Get the element that controls scrolling in the app
    const scrollElement: any = document.getElementById("content_area");

    // Disable scrolling when the user is not logged in and the modal is being shown
    if (!isLoggedIn && isModal && !isScrollAvailable && scrollElement) {
      scrollElement.style.overflow = "hidden"; // Lock scroll
    } else if (scrollElement) {
      scrollElement.style.overflow = ""; // Re-enable scroll
    }

    // Cleanup function to reset scroll settings when the component is unmounted or when `isModal` changes
    return () => {
      if (scrollElement) {
        scrollElement.style.overflow = ""; // Reset scroll
      }
    };
  }, [isModal, isLoggedIn, isScrollAvailable]); // Dependencies: re-run when any of these values change

  // If the user is not logged in, handle redirection or show the authentication modal
  if (!isLoggedIn) {
    return isModal ? (
      // If modal is true, show the AuthModal component and also render children (to allow other UI components like overlays)
      <>
        <AuthModal isFullPage={isFullPage} /> {/* Show authentication modal */}
        {children} {/* Render children under the modal */}
      </>
    ) : (
      // If not using a modal, redirect to the home page
      <Navigate to={"/"} replace />
    );
  }

  // If the user is logged in, render the children components normally
  return <>{children}</>;
};
